import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import * as PortfolioAnalyticsActions from '../../portfolio-analytics/store/actions/portfolio-analytics.actions';
import {
  workspaceTypes as types,
  ClearWorkspaceUserAction,
  LoadWorkspaceInvitesAction,
  LoadWorkspaceInvitesFailsAction,
  LoadWorkspaceInvitesSuccessAction,
  LoadWorkspaceRolesAction,
  LoadWorkspaceRolesFailsAction,
  LoadWorkspaceRolesSuccessAction,
  LoadWorkspaceSettingsAction,
  LoadWorkspaceSettingsFailsAction,
  LoadWorkspaceSettingsSuccessAction,
  LoadWorkspaceUserAction,
  LoadWorkspaceUserFailsAction,
  LoadWorkspaceUsersAction,
  LoadWorkspaceUsersFailsAction,
  LoadWorkspaceUsersSuccessAction,
  LoadWorkspaceUserSuccessAction,
  LoadCurrentUserWorkspacesAction,
  LoadCurrentUserWorkspacesFailsAction,
  LoadCurrentUserWorkspacesSuccessAction,
  ResendWorkspaceInviteAction,
  ResendWorkspaceInviteFailsAction,
  ResendWorkspaceInviteSuccessAction,
  RevokeWorkspaceInviteAction,
  RevokeWorkspaceInviteFailsAction,
  RevokeWorkspaceInviteSuccessAction,
  SaveWorkspaceInviteAction,
  SaveWorkspaceInviteFailsAction,
  SaveWorkspaceInviteSuccessAction,
  SaveWorkspaceSettingsAction,
  SaveWorkspaceSettingsFailsAction,
  SaveWorkspaceSettingsSuccessAction,
  SaveWorkspaceUserAction,
  SaveWorkspaceUserFailsAction,
  SaveWorkspaceUserSuccessAction,
  LoadSelectedWorkspaceSuccessAction,
  LoadSelectedWorkspaceFailsAction,
  LoadSelectedWorkspaceAction,
  ToggleWorkspaceUserActiveStatusAction,
  ToggleWorkspaceUserActiveStatusSuccessAction,
  ToggleWorkspaceUserActiveStatusFailsAction,
} from './workspace.actions';
import { catchError, map, mergeMap, pairwise, tap, withLatestFrom } from 'rxjs/operators';
import { WorkspaceService } from '../shared/services/workspace.service';
import { Workspace } from '../../shared';
import { WorkspaceUser } from '../../shared';
import { Invitation, Role } from '../shared/models';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { selectUrl } from 'app/shared/redux/router/router.selectors';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducers';
import { AdminService } from 'app/internal/admin/shared/services/admin.service';
import { LoadSelectedPropertyAction } from 'app/internal/property/property-store/property.actions';
import { LoadPropertyRequest } from 'app/internal/property/shared/models';
import { getWorkspacePropertiesForCurrentUser, getWorkspaceSettings } from './workspace.selectors';

@Injectable()
export class WorkspaceEffects {

  constructor(private actions$: Actions,
    private workspaceService: WorkspaceService,
    private adminService: AdminService,
    private router: Router,
    private toastr: ToastrService,
    private store: Store<AppState>) { }

  // Settings
  loadSettings$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_WORKSPACE_SETTINGS),
    map(action => (action as LoadWorkspaceSettingsAction).payload),
    mergeMap(() =>
      this.workspaceService.getWorkspaceSettings()
        .pipe(
          map((response: Workspace) => new LoadWorkspaceSettingsSuccessAction(response)),
          catchError((error) => of((new LoadWorkspaceSettingsFailsAction(error))))
        )
    )
  ));

  loadSettingsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(types.LOAD_WORKSPACE_SETTINGS_SUCCESS),
      withLatestFrom(
        this.store.select(getWorkspaceSettings)
      ),
      map(([_, state]) => state),
      pairwise(),  // Pair current state with the previous one      
      tap(([prevState, currState]) => {

        // If the SubscriptionPlan changed, need to do some cleanup...
        if (prevState?.subscriptionPlanId !== currState?.subscriptionPlanId) {
          // Clears the Component Grid filters, in case the user loads a Club vs HOA subscription.
          // This is b/c the Component Inventory grid can have different columns between the subscription type setting
          localStorage.removeItem('component-inventory-grid');
        }
      })
    ), { dispatch: false }
  );

  saveSettings$ = createEffect(() => this.actions$.pipe(
    ofType(types.SAVE_WORKSPACE_SETTINGS),
    map(action => (action as SaveWorkspaceSettingsAction).payload),
    mergeMap((settings: Workspace) =>
      this.workspaceService.updateWorkspaceSettings(settings)
        .pipe(
          map((response: Workspace) => new SaveWorkspaceSettingsSuccessAction(response)),
          catchError((error) => of((new SaveWorkspaceSettingsFailsAction(error))))
        )
    )
  ));

  saveSettingsSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(types.SAVE_WORKSPACE_SETTINGS_SUCCESS),
    map((action: SaveWorkspaceSettingsSuccessAction) => action.payload),
    tap(() => {
      this.toastr.success('Settings Saved');
    }),
    mergeMap((updatedWorkspace) => [
      new LoadWorkspaceSettingsSuccessAction(updatedWorkspace)
    ]),
  ));

  /* Users */
  loadUsers$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_WORKSPACE_USERS),
    map(action => (action as LoadWorkspaceUsersAction).payload),
    mergeMap(() =>
      this.workspaceService.getUsers()
        .pipe(
          map((response: WorkspaceUser[]) => new LoadWorkspaceUsersSuccessAction(response)),
          catchError((error) => of((new LoadWorkspaceUsersFailsAction(error))))
        )
    )
  ));

  loadUser$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_WORKSPACE_USER),
    map(action => (action as LoadWorkspaceUserAction).payload),
    mergeMap((userId: string) =>
      this.workspaceService.getUser(userId)
        .pipe(
          map((response: WorkspaceUser) => new LoadWorkspaceUserSuccessAction(response)),
          catchError((error) => of((new LoadWorkspaceUserFailsAction(error))))
        )
    )
  ));

  saveUser$ = createEffect(() => this.actions$.pipe(
    ofType(types.SAVE_WORKSPACE_USER),
    map(action => (action as SaveWorkspaceUserAction).payload),
    mergeMap((user: WorkspaceUser) =>
      this.workspaceService.updateUser(user)
        .pipe(
          map((response: WorkspaceUser) => new SaveWorkspaceUserSuccessAction(response)),
          catchError((error) => of((new SaveWorkspaceUserFailsAction(error))))
        )
    )
  ));

  saveUserSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.SAVE_WORKSPACE_USER_SUCCESS),
      withLatestFrom(
        this.store.select(selectUrl),
      ),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      tap(([action, currentRoute]) => {

        this.toastr.success('User Details Saved');

        // Redirect back to the "old" Portfolio Users List page or the "new" Portfolio Users page
        if (currentRoute.includes('portfolio-analytics/users/edit')) {
          this.router.navigateByUrl('/useraccount/portfolio-analytics/users');
        } else if (currentRoute.includes('/useraccount/workspace/users')) {
          this.router.navigateByUrl('/useraccount/workspace/users');
        }
      }),
      map(() => new ClearWorkspaceUserAction()),
    );
  }, { dispatch: true });

  toggleWorkspaceUserActiveStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.TOGGLE_WORKSPACE_USER_ACTIVE_STATUS),
      map(action => (action as ToggleWorkspaceUserActiveStatusAction).payload),
      mergeMap((payload: { userId: string, isActive: boolean, workspaceId: string }) =>
        this.adminService.toggleWorkspaceUserActiveStatus(payload.userId, payload.workspaceId, payload.isActive)
          .pipe(
            map(() =>
              new ToggleWorkspaceUserActiveStatusSuccessAction({ isActive: payload.isActive, workspaceId: payload.workspaceId })
            ),
            catchError((error) => of(new ToggleWorkspaceUserActiveStatusFailsAction(error)))
          )
      )
    );
  });

  toggleWorkspaceUserActiveStatusSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(types.TOGGLE_WORKSPACE_USER_ACTIVE_STATUS_SUCCESS),
    map((action: ToggleWorkspaceUserActiveStatusSuccessAction) => action.payload),
    tap((payload) => {
      if (payload.isActive) {
        this.toastr.success('User Reactivated');
      } else {
        this.toastr.success('User Deactivated');
      }
    }),
    mergeMap(() => {
      return [
        new LoadWorkspaceUsersAction(),
      ];
    }),
  ));

  /* Roles */
  loadRoles$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_WORKSPACE_ROLES),
    map(action => (action as LoadWorkspaceRolesAction).payload),
    mergeMap(() =>
      this.workspaceService.getRoles()
        .pipe(
          map((response: Role[]) => new LoadWorkspaceRolesSuccessAction(response)),
          catchError((error) => of((new LoadWorkspaceRolesFailsAction(error))))
        )
    )
  ));

  /* Invites */
  loadInvites$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_WORKSPACE_INVITES),
    map(action => (action as LoadWorkspaceInvitesAction).payload),
    mergeMap(() =>
      this.workspaceService.getInvites()
        .pipe(
          map((response: Invitation[]) => new LoadWorkspaceInvitesSuccessAction(response)),
          catchError((error) => of((new LoadWorkspaceInvitesFailsAction(error))))
        )
    )
  ));

  inviteUser$ = createEffect(() => this.actions$.pipe(
    ofType(types.SAVE_WORKSPACE_INVITE),
    map(action => (action as SaveWorkspaceInviteAction).payload),
    mergeMap((invite: Invitation) =>
      this.workspaceService.inviteUser(invite)
        .pipe(
          map((response: Invitation) => new SaveWorkspaceInviteSuccessAction(response)),
          catchError((error) => of((new SaveWorkspaceInviteFailsAction(error))))
        )
    )
  ));

  inviteUserSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.SAVE_WORKSPACE_INVITE_SUCCESS),
      withLatestFrom(
        this.store.select(selectUrl),
      ),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      tap(([action, currentRoute]) => {

        this.toastr.success('User Invited');

        if (currentRoute !== '/useraccount/portfolio-analytics/users') {
          this.router.navigateByUrl('/useraccount/workspace/users/invites');
        }
      }),
      map(() => new LoadWorkspaceInvitesAction()),
    );
  }, { dispatch: true });

  resendInvite$ = createEffect(() => this.actions$.pipe(
    ofType(types.RESEND_WORKSPACE_INVITE),
    map(action => (action as ResendWorkspaceInviteAction).payload),
    mergeMap((workspaceUserInvitationId: string) =>
      this.workspaceService.resendInvite(workspaceUserInvitationId)
        .pipe(
          map(() => new ResendWorkspaceInviteSuccessAction()),
          catchError((error) => of((new ResendWorkspaceInviteFailsAction(error))))
        )
    )
  ));

  resendInviteSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(types.RESEND_WORKSPACE_INVITE_SUCCESS),
    map((action: ResendWorkspaceInviteSuccessAction) => action.payload),
    tap(() => {
      this.toastr.success('Invitation Resent');
    }),
    mergeMap(() => [
      new LoadWorkspaceInvitesAction()
    ]),
  ));

  revokeInvite$ = createEffect(() => this.actions$.pipe(
    ofType(types.REVOKE_WORKSPACE_INVITE),
    map(action => (action as RevokeWorkspaceInviteAction).payload),
    mergeMap((workspaceUserInvitationId: string) =>
      this.workspaceService.revokeInvite(workspaceUserInvitationId)
        .pipe(
          map(() => new RevokeWorkspaceInviteSuccessAction()),
          catchError((error) => of((new RevokeWorkspaceInviteFailsAction(error))))
        )
    )
  ));

  revokeInviteSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(types.REVOKE_WORKSPACE_INVITE_SUCCESS),
    map((action: RevokeWorkspaceInviteSuccessAction) => action.payload),
    tap(() => {
      this.toastr.success('Invitation Revoked');
    }),
    mergeMap(() => [
      new LoadWorkspaceInvitesAction()
    ]),
  ));

  // Workspaces
  loadUserWorkspaces$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_CURRENT_USER_WORKSPACES),
    map(action => (action as LoadCurrentUserWorkspacesAction).payload),
    mergeMap(() =>
      this.workspaceService.getCurrentUserWorkspaces()
        .pipe(
          map((response: Workspace[]) => new LoadCurrentUserWorkspacesSuccessAction(response)),
          catchError((error) => of((new LoadCurrentUserWorkspacesFailsAction(error))))
        )
    ))
  );

  loadSelectedWorkspace$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_SELECTED_WORKSPACE),
    map(action => (action as LoadSelectedWorkspaceAction).payload),
    mergeMap((workspaceId: string) =>
      this.workspaceService.getWorkspace(workspaceId)
        .pipe(
          map((response: Workspace) => new LoadSelectedWorkspaceSuccessAction(response)),
          catchError((error) => of((new LoadSelectedWorkspaceFailsAction(error))))
        )
    )
  ));

  loadSelectedWorkspaceSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.LOAD_SELECTED_WORKSPACE_SUCCESS),
      withLatestFrom(
        this.store.select(getWorkspacePropertiesForCurrentUser),
        this.store.select(selectUrl),
      ),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      mergeMap(([action, workspaceProperties, currentRoute]) => {
        const propertyId = workspaceProperties[0].propertyId;

        // This check will force a redirect to the Property Dashboard page only if the user is 
        // currently viewing one of the Property-specific modules (i.e. Projects, etc.)  Otherwise, the
        // wrong data was being shown!
        let isRedirect = false;
        let redirectUrl = '';
        if (currentRoute.includes('useraccount/portfolio-analytics/reports/project-spend/breakdown')) {
          isRedirect = true;
          redirectUrl = 'useraccount/portfolio-analytics/reports/project-spend';
        } else if (!currentRoute.includes('useraccount/portfolio-analytics')) {
          isRedirect = true;
        }

        return [
          PortfolioAnalyticsActions.resetState(),
          new LoadSelectedPropertyAction(new LoadPropertyRequest(propertyId, isRedirect, redirectUrl))
        ];
      })
    );
  }, { dispatch: true });

}
